/**
 * Boli Live - Watchlist Button Component
 *
 * eBay-style heart icon to save/unsave auctions.
 */

/* eslint-disable react-refresh/only-export-components */
import { useState, useEffect } from 'react';
import { Heart } from 'lucide-react';
import { cn } from '../lib/utils';

interface WatchlistButtonProps {
    auctionId: string;
    className?: string;
    size?: 'sm' | 'md' | 'lg';
}

// Simple localStorage-based watchlist (can be upgraded to user account later)
const WATCHLIST_KEY = 'boli_watchlist';

function getWatchlist(): string[] {
    try {
        const stored = localStorage.getItem(WATCHLIST_KEY);
        return stored ? JSON.parse(stored) : [];
    } catch {
        return [];
    }
}

function saveWatchlist(watchlist: string[]) {
    localStorage.setItem(WATCHLIST_KEY, JSON.stringify(watchlist));
}

export function WatchlistButton({ auctionId, className, size = 'md' }: WatchlistButtonProps) {
    const [isWatching, setIsWatching] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);

    useEffect(() => {
        const watchlist = getWatchlist();
        setIsWatching(watchlist.includes(auctionId));
    }, [auctionId]);

    const toggleWatchlist = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const watchlist = getWatchlist();
        let newWatchlist: string[];

        if (isWatching) {
            newWatchlist = watchlist.filter(id => id !== auctionId);
        } else {
            newWatchlist = [...watchlist, auctionId];
            setIsAnimating(true);
            setTimeout(() => setIsAnimating(false), 300);
        }

        saveWatchlist(newWatchlist);
        setIsWatching(!isWatching);
    };

    const sizeClasses = {
        sm: 'w-4 h-4',
        md: 'w-5 h-5',
        lg: 'w-6 h-6',
    };

    return (
        <button
            onClick={toggleWatchlist}
            className={cn(
                'p-1.5 rounded-full transition-all duration-200',
                'hover:bg-black/10 active:scale-95',
                isWatching ? 'text-red-500' : 'text-gray-400 hover:text-gray-600',
                className
            )}
            aria-label={isWatching ? 'Remove from watchlist' : 'Add to watchlist'}
        >
            <Heart
                className={cn(
                    sizeClasses[size],
                    'transition-transform duration-200',
                    isWatching && 'fill-current',
                    isAnimating && 'scale-125'
                )}
            />
        </button>
    );
}

// Hook to get watchlist count
export function useWatchlistCount() {
    const [count, setCount] = useState(0);

    useEffect(() => {
        const updateCount = () => {
            setCount(getWatchlist().length);
        };

        updateCount();
        window.addEventListener('storage', updateCount);
        return () => window.removeEventListener('storage', updateCount);
    }, []);

    return count;
}

// Hook to check if an auction is in watchlist
export function useIsWatching(auctionId: string) {
    const [isWatching, setIsWatching] = useState(false);

    useEffect(() => {
        setIsWatching(getWatchlist().includes(auctionId));
    }, [auctionId]);

    return isWatching;
}

// Get all watchlist IDs
export function getWatchlistIds(): string[] {
    return getWatchlist();
}
